import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { toast } from "sonner";
import {
  BarChart3, RefreshCw, Zap, Shield, LogOut, Package, CreditCard,
  ArrowRight, User, Power, Settings, Key, Globe, X, Bitcoin, Copy, Loader2, Check, MessageSquare, AlertTriangle
} from "lucide-react";

const PRODUCT_ICONS = { "lead-scout": BarChart3, "ghost-recovery": RefreshCw, "smart-sync": Zap, "edge-guardian": Shield };
const TOOL_ROUTES = { "lead-scout": "/tools/lead-scout", "ghost-recovery": "/tools/ghost-recovery", "smart-sync": "/tools/smart-sync", "edge-guardian": "/tools/edge-guardian" };

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configModal, setConfigModal] = useState(null);
  const [configForm, setConfigForm] = useState({ api_key: "", store_url: "" });
  const [saving, setSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [cryptoSession, setCryptoSession] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const [feedbackForm, setFeedbackForm] = useState({ type: "feature_request", title: "", description: "" });
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  const isTrialActive = user?.trial_ends_at ? new Date(user.trial_ends_at) > new Date() : false;
  const trialDaysLeft = user?.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  useEffect(() => {
    Promise.all([
      API.get("/user/purchases").then(r => setPurchases(r.data)),
      API.get("/user/transactions").then(r => setTransactions(r.data)),
      API.get("/products").then(r => setAllProducts(r.data)),
      API.get("/automation/configs").then(r => setConfigs(r.data)).catch(() => {}),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const handlePurchase = (productId) => {
    if (!user) { navigate("/login"); return; }
    setSelectedProductId(productId);
    setShowModal(true);
  };

  const handlePaytrCheckout = async () => {
    try {
      const { data } = await API.post("/checkout/paytr/create", { product_id: selectedProductId, origin_url: window.location.origin });
      window.location.href = data.iframe_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    }
  };

  const handleCryptoCheckout = async () => {
    try {
      const { data } = await API.post("/checkout/crypto/create", { product_id: selectedProductId });
      setCryptoSession(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Crypto checkout failed");
    }
  };

  const verifyCryptoPayment = async () => {
    if (!cryptoSession) return;
    setVerifying(true);
    try {
      const { data } = await API.get(`/checkout/crypto/verify/${cryptoSession.session_id}`);
      if (data.status === "paid") {
        toast.success("Payment verified successfully!");
        navigate(`/payment/success?session_id=${cryptoSession.session_id}`);
      } else {
        toast.info("Payment not found yet. It might take a few minutes for the network to confirm.");
      }
    } catch (e) {
      toast.error("Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const openConfig = (product) => {
    const cfg = configs.find(c => c.product_id === product.id) || {};
    setConfigForm({ api_key: cfg.api_key || "", store_url: cfg.store_url || "" });
    setConfigModal(product);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await API.put(`/automation/config/${configModal.id}`, configForm);
      const { data } = await API.get("/automation/configs");
      setConfigs(data);
      toast.success("Configuration saved!");
      setConfigModal(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const toggleAutomation = async (productId) => {
    try {
      const { data } = await API.post(`/automation/toggle/${productId}`);
      setConfigs(prev => prev.map(c => c.product_id === productId ? { ...c, is_active: data.is_active } : c));
      toast.success(data.is_active ? "Automation activated!" : "Automation paused");
    } catch (e) { toast.error(e.response?.data?.detail || "Toggle failed"); }
  };

  const cancelSubscription = async (productId, productName) => {
    if (!window.confirm(`Are you sure you want to cancel ${productName}? You will lose access immediately.`)) return;
    try {
      await API.post(`/subscription/cancel/${productId}`);
      setPurchases(prev => prev.filter(p => p.id !== productId));
      setConfigs(prev => prev.filter(c => c.product_id !== productId));
      const { data } = await API.get("/products");
      setAllProducts(data);
      toast.success(`${productName} subscription cancelled`);
    } catch (e) {
      toast.error("Cancellation failed");
    }
  };
  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!feedbackForm.title || !feedbackForm.description) return toast.error("Please fill title and details.");
    setFeedbackSubmitting(true);
    try {
      await API.post("/feedback", feedbackForm);
      toast.success("Thank you for your feedback! Team NoRespect noted it.");
      setFeedbackForm({ type: "feature_request", title: "", description: "" });
    } catch (e) { toast.error("Failed to submit feedback."); }
    finally { setFeedbackSubmitting(false); }
  };

  const getConfig = (productId) => configs.find(c => c.product_id === productId) || {};
  const accessibleProducts = allProducts.filter(p => isTrialActive || purchases.some(purch => purch.id === p.id));
  const unpurchased = allProducts.filter(p => !purchases.find(pp => pp.id === p.id));

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <div className="w-6 h-6 border-2 border-[#00FF41] border-t-transparent animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0F0]" data-testid="dashboard-page">
      <header className="bg-[#0A0A0A] border-b border-[#1E1E1E] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading text-xl font-black tracking-tighter uppercase" data-testid="dashboard-logo">NRD<span className="text-[#00FF41]">.</span></Link>
          <div className="flex items-center gap-4">
            {user?.role === "admin" && (
              <Link to="/admin" className="text-xs font-mono text-[#00FF41] border border-[#00FF41]/30 px-3 py-1 hover:bg-[#00FF41]/10 transition-colors" data-testid="admin-panel-link">ADMIN</Link>
            )}
            <div className="flex items-center gap-2 text-sm">
              <User size={16} className="text-[#00FF41]" />
              <span className="font-medium" data-testid="dashboard-user-name">{user?.name}</span>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#00FF41] transition-colors" data-testid="dashboard-logout-btn">
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41] mb-1">Dashboard</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="dashboard-title">Welcome back, {user?.name}</h1>
        </div>

        {/* Trial Banner */}
        {user?.trial_ends_at && (
          <div className={`mb-8 p-4 flex items-center justify-between border ${isTrialActive ? 'border-[#00FF41] bg-[#00FF41]/5' : 'border-red-900 bg-red-900/10'}`}>
            <div className="flex items-center gap-3">
              {isTrialActive ? <Zap size={20} className="text-[#00FF41]" /> : <AlertTriangle size={20} className="text-red-400" />}
              <div>
                <p className={`font-heading font-bold ${isTrialActive ? 'text-[#00FF41]' : 'text-red-400'}`}>
                  {isTrialActive ? `14-Day Free Trial Active! (${trialDaysLeft} days remaining)` : "Free Trial Expired"}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {isTrialActive ? "All tools unlocked. Build and scale your operations instantly." : "Unlock continued optimization by securing a premium plan today."}
                </p>
              </div>
            </div>
            {isTrialActive ? (
               <span className="text-xs font-mono px-3 py-1 border border-[#00FF41] text-[#00FF41] font-bold uppercase tracking-widest animate-pulse">Trial Running</span>
            ) : (
               <a href="#expand-stack" className="text-xs font-bold px-4 py-2 bg-red-900 text-white hover:bg-red-800 transition-colors">SUBSCRIBE NOW</a>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[#1E1E1E] bg-[#111111] mb-8">
          <div className="p-6 border-r border-[#1E1E1E]">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">Active Tools</p>
            <p className="font-heading text-3xl font-black text-[#00FF41]" data-testid="dashboard-active-count">{configs.filter(c => c.is_active).length}</p>
          </div>
          <div className="p-6 border-r border-[#1E1E1E]">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">Purchased</p>
            <p className="font-heading text-3xl font-black">{purchases.length}</p>
          </div>
          <div className="p-6 border-r border-[#1E1E1E]">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">Transactions</p>
            <p className="font-heading text-3xl font-black">{transactions.length}</p>
          </div>
          <div className="p-6">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">Total Spent</p>
            <p className="font-heading text-3xl font-black text-[#00FF41]">${transactions.filter(t => t.payment_status === "paid").reduce((s, t) => s + t.amount, 0).toFixed(0)}</p>
          </div>
        </div>

        {/* Automation Control Panel */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Power size={18} className="text-[#00FF41]" />
            <h2 className="font-heading text-xl font-medium tracking-tight">Automation Control Panel</h2>
          </div>
          {accessibleProducts.length === 0 ? (
            <div className="bg-[#111111] border border-[#1E1E1E] p-8 text-center">
              <p className="text-sm text-slate-500 mb-4">Acquire tools to supercharge your store operations.</p>
              <Link to="/#pricing" className="inline-flex items-center gap-2 bg-[#00FF41] text-[#0A0A0A] px-5 py-2 text-sm font-bold hover:bg-[#00CC33] transition-colors" data-testid="dashboard-browse-btn">
                Explore Toolkit <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {accessibleProducts.map(p => {
                const isTrialAccess = !purchases.find(pr => pr.id === p.id) && isTrialActive;
                const Icon = PRODUCT_ICONS[p.id] || Package;
                const cfg = getConfig(p.id);
                const isActive = cfg.is_active;
                const hasKey = !!cfg.api_key;
                return (
                  <div key={p.id} className="bg-[#111111] border border-[#1E1E1E] p-5 premium-card-hover hover:bg-[#151515]" data-testid={`automation-panel-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 flex items-center justify-center border ${isActive ? 'border-[#00FF41] shadow-[0_0_10px_rgba(0,255,65,0.3)]' : 'border-[#1E1E1E]'}`}>
                          <Icon size={20} className={isActive ? "text-[#00FF41]" : "text-slate-500"} />
                        </div>
                        <div>
                          <h3 className="font-heading text-lg font-medium tracking-tight">{p.name}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            <span className={`text-xs font-mono px-2 py-0.5 ${isActive ? 'bg-[#00FF41]/10 text-[#00FF41] border border-[#00FF41]/30' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>
                              {isActive ? "RUNNING" : "STOPPED"}
                            </span>
                            {isTrialAccess && (
                              <span className="text-xs font-mono px-2 py-0.5 border border-dashed border-[#00FF41]/60 text-[#00FF41] flex items-center gap-1">
                                <Zap size={10} /> TRIAL
                              </span>
                            )}
                            {hasKey && <span className="text-xs text-slate-500 flex items-center gap-1"><Key size={10} /> API Key configured</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => openConfig(p)}
                          className="flex items-center gap-1.5 border border-[#1E1E1E] px-3 py-2 text-xs font-medium text-slate-300 hover:border-[#00FF41] hover:text-[#00FF41] transition-colors"
                          data-testid={`config-btn-${p.id}`}>
                          <Settings size={14} /> Configure
                        </button>
                        <Link to={TOOL_ROUTES[p.id] || "#"}
                          className="flex items-center gap-1.5 border border-[#00FF41]/50 px-3 py-2 text-xs font-medium text-[#00FF41] hover:bg-[#00FF41] hover:text-[#0A0A0A] transition-colors"
                          data-testid={`open-tool-${p.id}`}>
                          <ArrowRight size={14} /> Open Tool
                        </Link>
                        <button onClick={() => toggleAutomation(p.id)}
                          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold transition-colors ${isActive
                            ? 'bg-red-900/30 text-red-400 border border-red-800 hover:bg-red-900/50'
                            : 'bg-[#00FF41] text-[#0A0A0A] hover:bg-[#00CC33]'
                          }`}
                          data-testid={`toggle-btn-${p.id}`}>
                          <Power size={14} /> {isActive ? "Stop" : "Start"}
                        </button>
                        {!isTrialAccess && (
                          <button onClick={() => cancelSubscription(p.id, p.name)}
                            className="flex items-center gap-1.5 border border-red-900/50 px-3 py-2 text-xs text-red-400/70 hover:text-red-400 hover:border-red-800 transition-colors"
                            data-testid={`cancel-sub-btn-${p.id}`}>
                            <X size={14} /> Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Config Modal */}
        {configModal && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" data-testid="config-modal">
            <div className="bg-[#111111] border border-[#1E1E1E] p-8 w-full max-w-md">
              <h3 className="font-heading text-xl font-medium tracking-tight mb-1">Configure {configModal.name}</h3>
              <p className="text-xs text-slate-500 mb-6">Enter your credentials to connect the automation.</p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">
                    <Key size={12} className="inline mr-1" /> API Key
                  </label>
                  <input type="password" value={configForm.api_key}
                    onChange={e => setConfigForm(p => ({ ...p, api_key: e.target.value }))}
                    placeholder="Enter your API key"
                    className="w-full bg-[#0A0A0A] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41] transition-colors"
                    data-testid="config-api-key-input" />
                </div>
                {configModal.config_fields?.includes("store_url") && (
                  <div>
                    <label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">
                      <Globe size={12} className="inline mr-1" /> Store URL
                    </label>
                    <input type="url" value={configForm.store_url}
                      onChange={e => setConfigForm(p => ({ ...p, store_url: e.target.value }))}
                      placeholder="https://yourstore.myshopify.com"
                      className="w-full bg-[#0A0A0A] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41] transition-colors"
                      data-testid="config-store-url-input" />
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setConfigModal(null)} className="flex-1 border border-[#1E1E1E] py-2.5 text-sm font-medium text-slate-400 hover:border-slate-500 transition-colors" data-testid="config-cancel-btn">Cancel</button>
                <button onClick={saveConfig} disabled={saving} className="flex-1 bg-[#00FF41] text-[#0A0A0A] py-2.5 text-sm font-bold hover:bg-[#00CC33] transition-colors disabled:opacity-50" data-testid="config-save-btn">
                  {saving ? "Saving..." : "Save Configuration"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expand Stack */}
        {unpurchased.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-xl font-medium tracking-tight mb-4">Expand Your Stack</h2>
            <div className="grid md:grid-cols-2 gap-0 border border-[#1E1E1E] bg-[#111111]">
              {unpurchased.map((p, i) => {
                const Icon = PRODUCT_ICONS[p.id] || Package;
                return (
                  <div key={p.id} className={`p-6 ${i % 2 === 0 ? 'border-r border-[#1E1E1E]' : ''} ${i < unpurchased.length - 2 ? 'border-b border-[#1E1E1E]' : ''}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 flex items-center justify-center border border-[#1E1E1E]"><Icon size={20} className="text-slate-500" /></div>
                        <div>
                          <h3 className="font-heading text-lg font-medium tracking-tight">{p.name}</h3>
                          <p className="text-xs text-slate-500">{p.tagline} &mdash; ${p.price}/mo</p>
                        </div>
                      </div>
                      <button onClick={() => handlePurchase(p.id)} className="bg-[#00FF41] text-[#0A0A0A] px-4 py-2 text-xs font-bold hover:bg-[#00CC33] transition-colors" data-testid={`dashboard-purchase-${p.id}`}>Subscribe</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Transactions */}
        {transactions.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <CreditCard size={18} className="text-[#00FF41]" />
              <h2 className="font-heading text-xl font-medium tracking-tight">Transaction History</h2>
            </div>
            <div className="bg-[#111111] border border-[#1E1E1E] overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#1E1E1E] bg-[#0A0A0A]">
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Product</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Amount</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Status</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t, i) => (
                    <tr key={i} className="border-b border-[#1E1E1E] last:border-0" data-testid={`transaction-row-${i}`}>
                      <td className="p-4 font-medium">{t.product_id}</td>
                      <td className="p-4 text-[#00FF41]">${t.amount}</td>
                      <td className="p-4">
                        <span className={`text-xs font-mono px-2 py-1 ${t.payment_status === 'paid' ? 'bg-[#00FF41]/10 text-[#00FF41] border border-[#00FF41]/30' : 'bg-yellow-900/30 text-yellow-400 border border-yellow-800'}`}>
                          {t.payment_status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-4 text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Feedback & Feature Requests */}
        <div className="mt-12 border border-[#1E1E1E] bg-[#111111] p-8">
          <div className="flex items-center gap-2 mb-6">
            <MessageSquare size={18} className="text-[#00FF41]" />
            <h2 className="font-heading text-xl font-medium tracking-tight">Missing A Feature?</h2>
          </div>
          <p className="text-sm text-slate-400 mb-6 max-w-2xl">Found a bug? Have a dream automation feature kanka? Let us know exactly what this platform needs next and we will code it.</p>
          
          <form onSubmit={submitFeedback} className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-mono text-slate-500 uppercase tracking-widest mb-2">Report Type</label>
              <select
                value={feedbackForm.type}
                onChange={(e) => setFeedbackForm(prev => ({ ...prev, type: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[#1E1E1E] p-3 text-sm text-[#F0F0F0] focus:outline-none focus:border-[#00FF41]">
                <option value="feature_request">Feature Request ✨</option>
                <option value="bug">Report Bug 🐛</option>
                <option value="other">General Feedback 💡</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-mono text-slate-500 uppercase tracking-widest mb-2">Subject</label>
              <input
                type="text"
                placeholder="e.g. Add Telegram sales notifications"
                value={feedbackForm.title}
                onChange={(e) => setFeedbackForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[#1E1E1E] p-3 text-sm text-[#F0F0F0] focus:outline-none focus:border-[#00FF41] placeholder-slate-700"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-mono text-slate-500 uppercase tracking-widest mb-2">Details</label>
              <textarea
                rows={3}
                placeholder="Briefly explain the bug or detail the feature you want..."
                value={feedbackForm.description}
                onChange={(e) => setFeedbackForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[#1E1E1E] p-3 text-sm text-[#F0F0F0] focus:outline-none focus:border-[#00FF41] placeholder-slate-700 resize-none"
              ></textarea>
            </div>
            <div className="md:col-span-3 flex justify-end">
              <button 
                type="submit"
                disabled={feedbackSubmitting}
                className="bg-[#00FF41] text-[#0A0A0A] px-6 py-3 text-sm font-bold uppercase tracking-widest hover:bg-[#00CC33] flex items-center gap-2 disabled:opacity-50 transition-all duration-200">
                {feedbackSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                Send Request
              </button>
            </div>
          </form>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#0A0A0A] border border-[#1E1E1E] p-8 max-w-md w-full relative">
            <button onClick={() => { setShowModal(false); setCryptoSession(null); }} className="absolute top-4 right-4 text-slate-500 hover:text-white">✕</button>
            <h2 className="font-heading text-2xl font-bold mb-6">Select Payment Method</h2>
            
            {!cryptoSession ? (
              <div className="space-y-4">
                <button onClick={handlePaytrCheckout} className="w-full flex items-center justify-between bg-[#1E1E1E] hover:bg-[#2A2A2A] p-4 transition-colors">
                  <span className="flex items-center gap-3"><CreditCard size={20} className="text-[#00FF41]" /> Credit Card</span>
                  <ArrowRight size={16} className="text-slate-500" />
                </button>
                <button onClick={handleCryptoCheckout} className="w-full flex items-center justify-between bg-[#1E1E1E] hover:bg-[#2A2A2A] p-4 transition-colors">
                  <span className="flex items-center gap-3"><Bitcoin size={20} className="text-[#00FF41]" /> Crypto (USDT)</span>
                  <ArrowRight size={16} className="text-slate-500" />
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-yellow-500/10 border border-yellow-500/20 p-4 text-sm text-yellow-200">
                  <p className="font-bold mb-1">Important:</p>
                  <p>Send EXACTLY <strong>{cryptoSession.amount}</strong> {cryptoSession.currency} to the address below via the <strong>{cryptoSession.network}</strong> network.</p>
                </div>
                
                <div>
                  <label className="block text-xs text-slate-500 uppercase tracking-widest mb-2">Amount to send</label>
                  <div className="flex items-center justify-between bg-[#111] border border-[#1E1E1E] p-3">
                    <span className="font-mono text-[#00FF41] font-bold text-lg">{cryptoSession.amount} {cryptoSession.currency}</span>
                    <button onClick={() => copyToClipboard(cryptoSession.amount)} className="text-slate-400 hover:text-white"><Copy size={16}/></button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-slate-500 uppercase tracking-widest mb-2">Wallet Address ({cryptoSession.network})</label>
                  <div className="flex items-center justify-between bg-[#111] border border-[#1E1E1E] p-3 break-all">
                    <span className="font-mono text-sm text-slate-300">{cryptoSession.wallet_address}</span>
                    <button onClick={() => copyToClipboard(cryptoSession.wallet_address)} className="text-slate-400 hover:text-white ml-3 shrink-0"><Copy size={16}/></button>
                  </div>
                </div>

                <button 
                  onClick={verifyCryptoPayment} 
                  disabled={verifying}
                  className="w-full flex items-center justify-center gap-2 bg-[#00FF41] text-[#0A0A0A] py-3 font-bold hover:bg-[#00CC33] transition-colors disabled:opacity-50"
                >
                  {verifying ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  I Have Paid (Verify)
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
